/**
 * Plugin Base Interface
 *
 * All tracking plugins (GA4, Meta, LinkedIn, TikTok) implement this interface.
 */

import type { EventName, PluginResult, TrackingPlugin, UnifiedEvent } from "../types";

/**
 * Abstract base class for tracking plugins
 */
export abstract class BasePlugin implements TrackingPlugin {
  abstract name: string;

  abstract initialize(): Promise<void>;
  abstract supports(event: EventName): boolean;
  abstract track(event: UnifiedEvent): Promise<PluginResult>;
  abstract isReady(): boolean;

  /**
   * Helper to create a success result
   */
  protected success(data?: Record<string, unknown>): PluginResult {
    return { success: true, data };
  }

  /**
   * Helper to create an error result
   */
  protected error(error: string): PluginResult {
    return { success: false, error };
  }

  /**
   * Helper to wait for a condition
   */
  protected async waitFor(
    condition: () => boolean,
    timeout = 5000,
    interval = 100
  ): Promise<boolean> {
    const start = Date.now();
    while (Date.now() - start < timeout) {
      if (condition()) return true;
      await new Promise(resolve => setTimeout(resolve, interval));
    }
    return false;
  }
}

/**
 * Standard Meta event names for proper tracking
 */
export const META_STANDARD_EVENTS = [
  "PageView",
  "Lead",
  "Purchase",
  "CompleteRegistration",
  "AddToCart",
  "InitiateCheckout",
  "Search",
  "ViewContent",
  "AddPaymentInfo",
  "AddToWishlist",
  "Contact",
  "CustomizeProduct",
  "Donate",
  "FindLocation",
  "Schedule",
  "StartTrial",
  "SubmitApplication",
  "Subscribe",
] as const;

/**
 * Check if event is a Meta standard event
 */
export function isMetaStandardEvent(name: string): boolean {
  return META_STANDARD_EVENTS.includes(name as (typeof META_STANDARD_EVENTS)[number]);
}
